import type {Component, ComponentDefinition, Editor} from 'grapesjs';
import {isUserLoggedIn} from '@Libraries/user.library';

/**
 * Contains version-history plumbing shared by both email editors (the legacy GrapesJS maker's panel and the React email maker's view)
 */

const GET_TEMPLATE_VERSIONS_ENDPOINT = 'emailmaker/getTemplateVersions';
const GET_TEMPLATE_VERSION_CONTENT_ENDPOINT = 'emailmaker/getTemplateVersionContent';
const HAS_SEEN_ONBOARDING_ENDPOINT = 'user/hasUserSeenEmailVersionHistoryOnboarding';
const MARK_ONBOARDING_SEEN_ENDPOINT = 'user/updateUserHasSeenEmailVersionHistoryOnboarding';

let hasAttemptedVersionHistoryOnboarding = false;

export interface EmailVersionData {
  id: number;
  lastModifiedOn: number;
  editorName: string;
}

export interface CanvasState {
  components: ComponentDefinition[];
  styles: unknown;
}

interface ParsedTemplateContent {
  pages: string;
  styles: string;
}

interface ParsedPage {
  frames: {component: {components: ComponentDefinition[]}}[];
}

interface VersionHistoryUndoStack {
  models: unknown[];
  add: (models: unknown[]) => void;
  remove: (models: unknown[]) => void;
}

interface VersionHistorySession {
  baselinePointer: number;
  savedRedoTail: unknown[];
}

/**
 * Per-editor session state created on enter and removed on exit. Out-of-session calls no-op instead of
 * undoing down a stale baseline of 0, and a torn-down editor takes its session with it (WeakMap) rather
 * than leaving stale state behind for the next editor instance.
 */
const versionHistorySessionByEditor = new WeakMap<Editor, VersionHistorySession>();

const getUndoStack = (editor: Editor): VersionHistoryUndoStack => {
  return editor.UndoManager.getStack() as unknown as VersionHistoryUndoStack;
};

/**
 * Editing shortcuts (held with ⌘/Ctrl) that must be inert while viewing a version
 */
const READ_ONLY_BLOCKED_MODIFIER_KEYS = new Set(['z', 'c', 'v', 'x', 'd', 'l', 'b', 'i', 'u', 's']);
const READ_ONLY_BLOCKED_SHIFT_MODIFIER_KEYS = new Set(['r', 'j']);

/**
 * GrapesJS selection-navigation keys (no modifier) that should not move the canvas selection while viewing.
 */
const READ_ONLY_BLOCKED_NAV_KEYS = new Set(['tab', 'a', 's', 'd', 'w', 'escape']);

/**
 * Fetches the stored version-history metadata for the template, newest first.
 */
export const fetchEmailVersions = async (idEmailTemplate: string): Promise<EmailVersionData[]> => {
  const response = (await window.PMW.readLocal(GET_TEMPLATE_VERSIONS_ENDPOINT, {idEmailTemplate})) as EmailVersionData[];
  return Array.isArray(response) ? response : [];
};

export const fetchEmailVersionContent = async (idEmailTemplate: string, versionId: number): Promise<string> => {
  const response = (await window.PMW.readLocal(GET_TEMPLATE_VERSION_CONTENT_ENDPOINT, {idEmailTemplate, idVersion: versionId})) as {jsonContent?: string};
  if (typeof response?.jsonContent !== 'string') {
    throw new Error(`Could not fetch content for email version ${versionId}`);
  }
  return response.jsonContent;
};

/**
 * True when the keystroke targets a field the user can legitimately type into (e.g. a panel input), so the
 * read-only canvas lock must not swallow it.
 */
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
};

/**
 * Capture-phase keydown guard (installed while viewing) that stops GrapesJS' bubble-phase keymaps — notably ⌘/Ctrl+Z,
 * which would desync the preview tracking. Editing combos are also preventDefault-ed; nav keys only stopped so Tab still works.
 */
const blockEditingKeydownWhileViewing = (event: KeyboardEvent): void => {
  if (isEditableTarget(event.target)) {
    return;
  }

  const key = event.key.toLowerCase();
  const hasModifier = event.metaKey || event.ctrlKey;

  if (key === 'delete' || key === 'backspace') {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (hasModifier && (READ_ONLY_BLOCKED_MODIFIER_KEYS.has(key) || (event.shiftKey && READ_ONLY_BLOCKED_SHIFT_MODIFIER_KEYS.has(key)))) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  if (!hasModifier && READ_ONLY_BLOCKED_NAV_KEYS.has(key)) {
    event.stopImmediatePropagation();
  }
};

const lockCanvasForViewing = (editor: Editor): void => {
  const canvasBody = editor.Canvas.getBody();
  if (canvasBody) {
    canvasBody.style.pointerEvents = 'none';
  }
  document.addEventListener('keydown', blockEditingKeydownWhileViewing, true);
};

const unlockCanvasAfterViewing = (editor: Editor): void => {
  const canvasBody = editor.Canvas.getBody();
  if (canvasBody) {
    canvasBody.style.pointerEvents = '';
  }
  document.removeEventListener('keydown', blockEditingKeydownWhileViewing, true);
};

/**
 * Parses a stored version's jsonContent into the components + styles to apply to the canvas.
 */
export const getCanvasStateFromSnapshot = (jsonContent: string): CanvasState => {
  const parsed = JSON.parse(jsonContent) as ParsedTemplateContent;
  const pages = JSON.parse(parsed.pages) as ParsedPage[];
  const styles = JSON.parse(parsed.styles) as unknown;
  return {
    components: pages[0]?.frames[0]?.component.components ?? [],
    styles,
  };
};

/**
 * Starts a version-history session: anchors the undo baseline + redo tail and locks the canvas read-only.
 */
export const startVersionHistorySession = (editor: Editor): void => {
  const baselinePointer = editor.UndoManager.getPointer();
  versionHistorySessionByEditor.set(editor, {
    baselinePointer,
    savedRedoTail: getUndoStack(editor).models.slice(baselinePointer + 1),
  });
  lockCanvasForViewing(editor);
};

export const isVersionHistorySessionActive = (editor: Editor): boolean => {
  return versionHistorySessionByEditor.has(editor);
};

/**
 * Returns the canvas to the working design by undoing any active preview back down to the baseline pointer.
 * Undo re-inserts the stored original content wrapper, so the user's prior undo history stays valid.
 */
export const revertToCurrentVersion = (editor: Editor): void => {
  const session = versionHistorySessionByEditor.get(editor);
  if (!session) {
    return;
  }

  const undoManager = editor.UndoManager;
  while (undoManager.getPointer() > session.baselinePointer && undoManager.hasUndo()) {
    undoManager.undo();
  }
};

/**
 * Resolves the maker-specific wrapper handles previewVersion needs: the wrapper component type to match in a
 * snapshot and how to read the canvas' current main wrapper (the legacy and React makers expose these differently).
 */
export interface VersionWrapperResolver {
  wrapperType: string;
  getMainWrapper: (editor: Editor) => Component | undefined;
}

/**
 * Applies a stored version to the canvas as a transient preview: reverts any prior preview back to the working design,
 * then swaps in the version's wrapper and styles. The swap lands on the undo stack, so the user's undo history stays intact.
 */
export const previewVersion = (editor: Editor, jsonContent: string, resolver: VersionWrapperResolver): void => {
  if (!isVersionHistorySessionActive(editor)) {
    return;
  }

  const state = getCanvasStateFromSnapshot(jsonContent);
  revertToCurrentVersion(editor);

  const versionWrapper = state.components.find((component) => component.type === resolver.wrapperType);
  const currentWrapper = resolver.getMainWrapper(editor);
  if (versionWrapper && currentWrapper) {
    currentWrapper.replaceWith(versionWrapper);
  }
  editor.setStyle(state.styles);
};

/**
 * Removes every stack entry above the current pointer
 */
const clearRedoTailFromPreview = (editor: Editor): void => {
  const stack = getUndoStack(editor);
  const redoTail = stack.models.slice(editor.UndoManager.getPointer() + 1);
  if (redoTail.length > 0) {
    stack.remove(redoTail);
  }
};

/**
 * Re-attaches the user's pre-version-history redo entries (captured on enter) that previewing discarded
 */
const restoreUserRedoTail = (editor: Editor): void => {
  const session = versionHistorySessionByEditor.get(editor);
  if (!session) {
    return;
  }

  if (session.savedRedoTail.length > 0) {
    getUndoStack(editor).add(session.savedRedoTail);
    session.savedRedoTail = [];
  }
};

/**
 * Promotes the currently previewed content to the working design so ending the session keeps it instead of reverting.
 */
export const commitRestore = (editor: Editor): void => {
  const session = versionHistorySessionByEditor.get(editor);
  if (!session) {
    return;
  }

  session.savedRedoTail = [];
  session.baselinePointer = editor.UndoManager.getPointer();
};

/**
 * Tears down the version-history session: reconciles the redo stack (drops preview entries, re-attaches the
 * user's pre-session redo tail) and unlocks the canvas. Does not revert an active preview — callers decide
 * whether to revert or commit first.
 */
export const endVersionHistorySession = (editor: Editor): void => {
  clearRedoTailFromPreview(editor);
  restoreUserRedoTail(editor);
  versionHistorySessionByEditor.delete(editor);
  unlockCanvasAfterViewing(editor);
};

export const shouldShowVersionHistoryOnboarding = async (): Promise<boolean> => {
  if (hasAttemptedVersionHistoryOnboarding || !isUserLoggedIn()) {
    return false;
  }
  hasAttemptedVersionHistoryOnboarding = true;

  try {
    const response = (await window.PMW.readLocal(HAS_SEEN_ONBOARDING_ENDPOINT)) as {hasSeen: boolean};
    return !response.hasSeen;
  } catch (error) {
    console.error('Failed to check email version history onboarding status:', error);
    return false;
  }
};

export const markVersionHistoryOnboardingAsSeen = async (): Promise<void> => {
  try {
    await window.PMW.writeLocal(MARK_ONBOARDING_SEEN_ENDPOINT);
  } catch (error) {
    console.error('Failed to mark email version history onboarding as seen:', error);
  }
};
